import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { App } from '@df/core/app';
import { ElementHeightDirective } from '@df/dom/element-height/element-height.directive';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { RatingComponent } from '@df/ui/common/rating.component';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent } from '@icms/core/content';
import { Subscription } from 'rxjs';
import { AbstractCmsBlockComponent } from '../../../cms/block/abstract-cms-block.component';
import { MultisiteSwitchMobileComponent } from '../../../multisite/mobile/multisite-switch-mobile.component';
import { NewsletterFormComponent } from '../../../newsletter/newsletter-form.component';
import { AccordionComponent } from '../../../ui/accordion/accordion.component';
import { AccordionItemComponent } from '../../../ui/accordion/item/accordion-item.component';
import { ForceTargetBlankDirective } from '../../../ui/common/force-target-blank.directive';
import { PageFooterColumnMobileComponent } from './column/mobile/page-footer-column-mobile.component';
import { PageFooterColumnComponent } from './column/page-footer-column.component';
import { HideFooterService } from './hide-footer.service';
import { PAGE_FOOTER_CONFIG } from './page-footer.config';
import { NPageFooter } from './page-footer.interface';

@Component({
    selector: 'page-footer',
    templateUrl: 'page-footer.html',
    styleUrl: './page-footer.scss',
    standalone: true,
    imports: [
        AccordionComponent,
        AccordionItemComponent,
        CommonModule,
        DfImageDirective,
        ElementHeightDirective,
        ForceTargetBlankDirective,
        GridModule,
        HrefComponent,
        ICmsClientModule,
        IfSizeDirective,
        MultisiteSwitchMobileComponent,
        NewsletterFormComponent,
        PageFooterColumnComponent,
        PageFooterColumnMobileComponent,
        RatingComponent,
        ToggleModule,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageFooterComponent>(PAGE_FOOTER_CONFIG)
export class PageFooterComponent
    extends AbstractCmsBlockComponent
    implements NICmsContentComponent.IComponent, OnInit, AfterViewInit, OnDestroy
{
    @ICmsContent({
        type: 'blockContent'
    })
    override content!: NPageFooter.IBlockDataContent;

    protected override readonly blockDataUrl = NPageFooter.BLOCK_ID;

    private _newsletterIsVisible = false;
    hidden = false;
    copyrightDate = new Date().getFullYear();

    protected hideFooterService = inject(HideFooterService);
    protected app = inject(App);

    protected hideFooterSubscription?: Subscription;

    override ngOnInit() {
        super.ngOnInit();

        this.hideFooterSubscription = this.hideFooterService.subscribe(hidden => {
            if (this.hidden !== hidden) {
                this.hidden = hidden;

                this.detectChanges();
            }
        });
    }

    ngAfterViewInit() {
        this.timeoutService.setTimeout(() => {
            this.applyStyles();
            this.detectChanges();
        }, 2000);
    }

    override ngOnDestroy(): void {
        this.hideFooterSubscription?.unsubscribe();

        super.ngOnDestroy();
    }

    get newsletterIsVisible() {
        return this._newsletterIsVisible;
    }

    set newsletterIsVisible(value: boolean) {
        if (this._newsletterIsVisible !== value) {
            this._newsletterIsVisible = value;

            this.detectChanges();
        }
    }

    get rating(): number {
        if (this.app.data.reviews.rating_summary && this.app.data.reviews.reviews_count) {
            return this.app.data.reviews.rating_summary / 20;
        }

        return 0;
    }

    showNewsletter() {
        this.newsletterIsVisible = true;
    }
}
